import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { showToast } from "../../../Utills/toast";
import styled from "styled-components";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  createForBusinessImage,
  getForBusinessImage,
  updateForBusinessImage,
} from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import removeIcon from "../../../Assets/remove-image-photo.svg";
import FormGroup from "../../Common/FormGroup";

// Validation Schema
const validationSchema = Yup.object({
  paragraph: Yup.string()
    .required("Paragraph is required"),
  image: Yup.mixed()
    .test("required", "Header image is required", function (value) {
      const { old_image } = this.parent;
      return !!(value || old_image);
    }),
});

export default function HeaderImgEdit() {
  const { id, partnerId } = useParams();
  const [data, setHeader] = useState({
    paragraph: "",
    image: "",
    index: null,
  });
  const inputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Initial form values
  const getInitialValues = () => ({
    paragraph: data.paragraph || "",
    image: data.image || "",
    old_image: data.image || "",
  });

  const fetchHeaderImage = async () => {
    setIsLoading(true);
    if (!Number(id)) {
      setIsLoading(false);
      return;
    }      
    try {
      const res = await getForBusinessImage(id);
      console.log(res);
      const headerImgData = res.data.data[0];
      setHeader({
        ...headerImgData,
        index: res.data.data.indexOf(headerImgData),
      });
    } catch (error) {
      console.log(error);
      showToast({
        type: 'error',
        message: 'Failed to fetch header data. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHeaderImage();
  }, [id]);

  const handleSubmit = async (values, { setFieldValue }) => {
    setSaving(true);

    var formData = new FormData();
    formData.append("paragraph", values.paragraph);
    
    if (values.image instanceof File) {
      formData.append("image", values.image);
    }

    if (!Number(id)) {
      try {
        formData.append("partner_id", partnerId);
        const res = await createForBusinessImage(formData);
        console.log(res);
        
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || 'Header section has been created successfully!',
          });
          const headerImgData = res.data.data;
          setHeader(headerImgData);
          if (headerImgData && headerImgData.id) {
            navigate(`/${partnerId}/forbusiness/headerimg/edit/${headerImgData.id}`);
          }
        } else {
          showToast({
            type: 'error',
            message: res?.data?.message || 'Failed to create header section. Please try again.',
          });
        }
      } catch (err) {
        console.log(err);
        showToast({
          type: 'error',
          message: err?.response?.data?.message || 'Failed to create header section. Please try again.',
        });
      }
    } else {
      try {
        const res = await updateForBusinessImage(id, formData);
        console.log(res);
        
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || 'Header section has been updated successfully!',
          });
          
          // Update the form fields with the returned image URL if available
          const updatedHeaderData = res?.data?.data;
          if (updatedHeaderData) {
            if (updatedHeaderData.image) {
              setFieldValue("image", updatedHeaderData.image);
              setFieldValue("old_image", updatedHeaderData.image);
            }
            if (updatedHeaderData.paragraph) {
              setFieldValue("paragraph", updatedHeaderData.paragraph);
            }
            setHeader(prev => ({
              ...prev,
              ...updatedHeaderData,
            }));
          }
          
          // Clear the file input
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        } else {
          showToast({
            type: 'error',
            message: res?.data?.message || 'Failed to update header section. Please try again.',
          });
        }
      } catch (err) {
        showToast({
          type: 'error',
          message: err?.response?.data?.message || 'Failed to update header section. Please try again.',
        });
        console.log(err);
      }
    }
    
    setSaving(false);
  };

  return (
   <>
    {isLoading ? <CmsCircularProgress/> : 
    <FormWrapper>
      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          errors,
          touched,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          getFieldProps,
        }) => (
          <Form noValidate onSubmit={handleSubmit}>
            <Title>{Number(id) ? 'Edit' : 'Add' } header section</Title>
            <SliderDiv>
              <FormGroup 
                label='Enter a new paragraph' 
                requiredField
                error={touched.paragraph && errors.paragraph ? errors.paragraph : ''}
              >
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Enter your paragraph here..."
                  {...getFieldProps('paragraph')}
                  isInvalid={touched.paragraph && !!errors.paragraph}
                />
              </FormGroup>

              <FormGroup
                label='Select a new Image'
                requiredField
                error={touched.image && errors.image ? errors.image : ''}
              >
                <Form.Control
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    setFieldValue("image", file);
                  }}
                  onBlur={getFieldProps('image').onBlur}
                  style={{ display: "none" }}
                  ref={inputRef}
                  name="image"
                  isInvalid={touched.image && !!errors.image}
                />
                {values.image ? (
                  <ImageWrapper>
                    <UploadImage>
                      <BootstrapImage
                        src={values.image instanceof File ? URL.createObjectURL(values.image) : values.image}
                        alt="header"
                        onClick={() => {
                          inputRef.current.click();
                        }}
                      />
                    </UploadImage>
                    {(values.image instanceof File || (values.image && values.image !== values.old_image)) && (
                      <RemoveButtonWrapper>
                        <BootstrapButton 
                          type="button"
                          onClick={() => {
                            setFieldValue("image", values.old_image || null);
                            if (inputRef.current) {
                              inputRef.current.value = '';
                            }
                          }}
                        >
                          <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
                        </BootstrapButton>
                      </RemoveButtonWrapper>
                    )}
                  </ImageWrapper>
                ) : (Number(id) && (values.image || values.old_image)) ? (
                  <UploadImage>
                    <BootstrapImage
                      src={values.image || values.old_image}
                      alt="header"
                      onClick={() => {
                        inputRef.current.click();
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.opacity = 0.8;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.opacity = 1;
                      }}
                    />
                  </UploadImage>
                ) : (
                  <div>
                    <Button
                      className="w-auto mb-0"
                      onClick={() => {
                        inputRef.current.click();
                      }}
                    >
                      Upload the image
                    </Button>
                  </div>
                )}
              </FormGroup>
              <br />
              <ButtonWrapper>
                <Link to={`/${partnerId}/forbusiness/headerimg/view/${Number(id) ? id : 'create'}`}>
                  <Button variant="secondary" className="w-100">Go Back</Button>
                </Link>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={saving || isSubmitting}
                  className="w-100"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </Button>
              </ButtonWrapper>
            </SliderDiv>
          </Form>
        )}
      </Formik>
    </FormWrapper>}
   </>
  );
}

const ImageWrapper = styled.div`
    position:relative;
    max-width: 15vw;
   @media (max-width:576px) {
   max-width: 40vw;
  }
`;

const FormWrapper = styled.div`
 @media (min-width:576px) {
   padding: 1rem;
  }
    @media (min-width:768px) {
   padding: 1.5rem;
  }
   padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
    @media (max-width: 890px) {
  gap: 2rem;
}
 @media (max-width: 425px) {
  gap: 1rem;
}
`;
const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  position: absolute;
    top: -17px;
    right: -14px;
       @media (max-width:576px) {
          top: -9px;
        right: -17px;
  }
`;
const UploadImage = styled.div`
  max-width: 15vw;
   overflow: hidden;
  border-radius: 6px;
  @media (max-width:576px) {
   max-width: 40vw;
`;
const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "15vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "40vw",
  },
}));
const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;
const  BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor:'#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #dc3545',
  "&:hover": {
    background: '#fffeff',
    boxShadow:'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));